import { coordOf, positionMilli } from "./board.js";
import { TOWER_OFFLINE_THRESHOLD_PERMILLE } from "./constants.js";
import { defenceBudget, towerStats, unitStats } from "./data.js";
import { distSq, mulPermille } from "./fixed.js";
import type { Coord, Layout, TowerKind, TowerStats, UnitKind, UnitStatus } from "./types.js";

/**
 * Tower targeting and firing (02 GDD §6.3).
 *
 * The whole point of this module is that "which unit does this tower shoot?"
 * has exactly one answer, arrived at the same way on the server and on the
 * phone. Every comparison below is a total order over integers, ending in the
 * unit id, so there is never a tie left for chance to settle.
 */

export interface TowerState {
  readonly id: number;
  /** Board tile the slot sits on. */
  readonly at: Coord;
  /** Tile centre in milli-tiles, so range maths matches unit positions. */
  readonly posMilli: Coord;
  readonly kind: TowerKind;
  readonly level: number;
  hp: number;
  readonly maxHp: number;
  /** Earliest tick this tower may fire again. */
  nextFireTick: number;
  /** Set by a Saboteur (S1-6). While in the future, the tower cannot fire. */
  disabledUntilTick: number;
  /** Last unit it shot at, for the replay to draw a beam. */
  targetId: number | null;
  /**
   * True once this tower fired, was damaged, or was disabled.
   *
   * This is exactly the set the attacker is allowed to see after a failed
   * attack (02 GDD §7.3): a tower that never did anything stayed hidden, so it
   * stays hidden in their replay too.
   */
  involved: boolean;
}

/**
 * The part of a unit the towers care about.
 *
 * Declared here rather than importing UnitState from simulate.ts, which would
 * make the two modules depend on each other. UnitState satisfies this shape.
 */
export interface Targetable {
  readonly id: number;
  readonly kind: UnitKind;
  readonly hp: number;
  readonly progress: number;
  readonly status: UnitStatus;
}

/**
 * Build the towers from the layout, in row-major order so ids never depend on
 * anything that happens during the battle.
 *
 * Tower HP carries the Wall's bonus (Defence Budget sheet): a defender who
 * levelled their Wall gets tougher towers as well as a longer road.
 */
export function buildTowers(layout: Layout): TowerState[] {
  const bonus = defenceBudget(layout.wallLevel).towerHpBonusPermille;
  const towers: TowerState[] = [];

  for (let i = 0; i < layout.tiles.length; i++) {
    const tile = layout.tiles[i];
    if (tile?.kind !== "tower_slot" || !tile.tower) continue;

    const at = coordOf(i, layout.size);
    const stats = towerStats(tile.tower.kind, tile.tower.level);
    const level = stats.levels[tile.tower.level - 1];
    if (!level) continue;

    const maxHp = level.hp + mulPermille(level.hp, bonus);
    towers.push({
      id: towers.length,
      at,
      posMilli: { x: at.x * 1000, y: at.y * 1000 },
      kind: tile.tower.kind,
      level: tile.tower.level,
      hp: maxHp,
      maxHp,
      nextFireTick: 0,
      disabledUntilTick: -1,
      targetId: null,
      involved: false,
    });
  }

  return towers;
}

/**
 * A tower below the offline threshold stops firing until it repairs
 * (Assumptions sheet). Nothing can damage a tower until S1-6, so this is
 * dormant today — but the rule belongs with the firing logic, not scattered
 * into it later.
 */
export function isOffline(tower: TowerState): boolean {
  return tower.hp * 1000 < tower.maxHp * TOWER_OFFLINE_THRESHOLD_PERMILLE;
}

/**
 * Scouts are not shot at.
 *
 * The Units sheet lists the Scout's counter as "Defender scouts", not towers,
 * and gives it 30 HP — a single Arrow Tower shot would delete it and make
 * scouting pointless. Treating it as untargetable is the reading that makes the
 * unit do its job. Flagged for confirmation (D-087).
 */
export function isTargetable(unit: Targetable): boolean {
  if (unit.status !== "walking" && unit.status !== "blocked") return false;
  return unitStats(unit.kind).combat !== false;
}

export interface TowerContext {
  readonly tick: number;
  readonly path: readonly Coord[];
  readonly units: readonly Targetable[];
}

/** True when the unit sits inside the tower's ring: within range, outside min range. */
export function inRange(tower: TowerState, stats: TowerStats, unitPos: Coord): boolean {
  const d2 = distSq(tower.posMilli.x, tower.posMilli.y, unitPos.x, unitPos.y);
  if (d2 > stats.rangeMilli * stats.rangeMilli) return false;
  // The Cannon cannot hit what is right under it (02 GDD §6.3).
  if (d2 < stats.minRangeMilli * stats.minRangeMilli) return false;
  return true;
}

/**
 * Pick this tower's target.
 *
 * Default rule: the unit furthest along the road — nearest the core, and so the
 * most urgent. Ties break by lowest HP (finish something off rather than spread
 * damage), then by lowest id so the order is total.
 *
 * The Frost Tower is the stated exception: it goes for the fastest unit in
 * range, because slowing a Rider is worth more than slowing a Ram.
 */
export function acquireTarget(
  tower: TowerState,
  context: TowerContext
): Targetable | null {
  const stats = towerStats(tower.kind, tower.level);
  let best: Targetable | null = null;

  for (const unit of context.units) {
    if (!isTargetable(unit)) continue;
    if (!inRange(tower, stats, positionMilli(context.path, unit.progress))) continue;
    if (best === null || preferred(tower.kind, unit, best)) best = unit;
  }

  return best;
}

/** True when `candidate` is a better target for this tower than `current`. */
function preferred(kind: TowerKind, candidate: Targetable, current: Targetable): boolean {
  if (kind === "frost") {
    const a = unitStats(candidate.kind).speedMilliPerTick;
    const b = unitStats(current.kind).speedMilliPerTick;
    // NOTE: this is the unit's base speed, not its speed right now. A Frost
    // Tower will therefore keep picking the same Rider even while it is already
    // slowed. 02 GDD §6.3 says "fastest unit in range" and this follows it
    // literally; worth revisiting if playtests show Frost wasting shots.
    if (a !== b) return a > b;
  }

  if (candidate.progress !== current.progress) return candidate.progress > current.progress;
  if (candidate.hp !== current.hp) return candidate.hp < current.hp;
  return candidate.id < current.id;
}

export interface FireOrder {
  readonly towerId: number;
  readonly unitId: number;
  /** The tower's raw damage at its level. Armour is applied in S1-5. */
  readonly damage: number;
}

/**
 * Step 6 of the tick: every tower whose cooldown has elapsed acquires a target
 * and fires. Returns the shots taken, in tower order.
 *
 * Firing does not yet hurt anybody — damage lands in S1-5. Keeping the two
 * apart means the targeting rules can be tested on their own, which is where
 * the subtle bugs live.
 */
export function fireTowers(towers: readonly TowerState[], context: TowerContext): FireOrder[] {
  const orders: FireOrder[] = [];

  for (const tower of towers) {
    if (context.tick < tower.disabledUntilTick) continue;
    if (isOffline(tower)) continue;
    if (context.tick < tower.nextFireTick) continue;

    const target = acquireTarget(tower, context);
    if (!target) {
      tower.targetId = null;
      continue;
    }

    const stats = towerStats(tower.kind, tower.level);
    const level = stats.levels[tower.level - 1];
    if (!level) continue;

    tower.targetId = target.id;
    tower.involved = true;
    tower.nextFireTick = context.tick + level.cooldownTicks;
    orders.push({ towerId: tower.id, unitId: target.id, damage: level.damage });
  }

  return orders;
}
